<template>
  <v-container grid-list-sm>
    <v-form ref="paymentform" lazy-validation>
    <v-layout row wrap>
      <v-card elevation="0" class="pt-0">
        <v-card-text fill-height class="pt-0">
          <v-container fluid grid-list-sm class="pa-0">
            <v-layout row wrap>
              <v-flex xs12 sm6>
                <v-text-field
                  :label="schedule.request_type === 'ach' ? 'Account Holder Name' : 'Card Holder Name'"
                  v-model="fields.card_name"
                  append-icon="fa-user"
                  :rules="nameRules"
                  box
                  hide-details
                  required
                ></v-text-field>
              </v-flex>

              <template v-if="schedule.request_type === 'ach'">
                <v-flex xs12 sm6>
                  <v-text-field
                    label="Account Number"
                    v-model="fields.ach_account_number"
                    append-icon="fa-institution"
                    :rules="accountRules"
                    :hint="'Leave blank to keep current account (**** ' + schedule.last_four + ')'"
                    persistent-hint
                    box
                  />
                </v-flex>
                <v-flex xs12 sm6>
                  <v-text-field
                    label="Routing Number"
                    v-model="fields.ach_routing_number"
                    append-icon="fa-exchange"
                    :rules="routingRules"
                    mask="#########"
                    box
                    hide-details
                    required
                  />
                </v-flex>
                <v-flex xs12 sm6>
                  <v-select
                    label="Type"
                    v-model="fields.ach_type"
                    :items="achTypes"
                    box
                    hide-details
                    required
                  />
                </v-flex>
              </template>

              <template v-else>
                <v-flex xs12 sm6>
                  <v-text-field
                    label="Card Number"
                    v-model="fields.card_number"
                    :append-icon="(!cardType || cardType==='unknown') ? 'fa-credit-card' : ''"
                    :mask="cardMask"
                    :rules="cardRules"
                    :hint="'Leave blank to keep current card (**** ' + schedule.last_four + ')'"
                    persistent-hint
                    box
                  >
                    <img slot="append" :src="'/static/cards/' + cardType + '.svg'" height="24px" v-if="cardType && cardType!=='unknown'" />
                  </v-text-field>
                </v-flex>
                <v-flex sm6 class="hidden-xs-only">
                </v-flex>
                <v-flex xs6 sm3>
                  <v-text-field
                    label="Expiration"
                    type="tel"
                    v-model="fields.card_expiration"
                    append-icon="fa-calendar"
                    mask="## / ##"
                    :rules="expirationRules"
                    box
                    hide-details
                    required
                  ></v-text-field>
                </v-flex>
              </template>
            </v-layout>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-container fluid grid-list-lg class="pa-0">
            <v-layout row wrap>
              <v-spacer></v-spacer>
              <v-flex xs3>
                <v-btn block large @click="closeModal">Cancel</v-btn>
              </v-flex>
              <v-flex xs3>
                <v-btn block large @click="save" color="primary" :disabled="!hasChanges">Confirm</v-btn>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-actions>
      </v-card>
    </v-layout>
    </v-form>
  </v-container>
</template>

<script>
import Bus from '@/bus'

import ApiMixin from '@/mixins/api'
import AuthMixin from '@/mixins/auth'
import DisplayMixin from '@/mixins/display'
import StoreMixin from '@/store/mixin'
import UtilityMixin from '@/mixins/utility'
import ValidationMixin from '@/mixins/validation'

export default {

  mixins: [
    ApiMixin,
    AuthMixin,
    DisplayMixin,
    StoreMixin,
    UtilityMixin,
    ValidationMixin
  ],

  data: function () {
    return {
      fields: {
        card_name: '',
        card_name_original: '',
        card_number: '',
        card_expiration: '',
        card_expiration_original: '',
        ach_routing_number: '',
        ach_routing_number_original: '',
        ach_account_number: '',
        ach_type: 'CHECKING',
        ach_type_original: 'CHECKING'
      },

      achTypes: [
        {value: 'CHECKING', text: 'Personal checking'},
        {value: 'SAVINGS', text: 'Personal savings'},
        {value: 'BUSINESSCK', text: 'Business checking'}
      ],

      nameRules: [
        (v) => !!v || 'Name is required'
      ],
      cardRules: [
        (v) => !v || this.validateCardNumber(v) || 'Card number is invalid'
      ],
      expirationRules: [
        (v) => !!v || 'Expiration is required'
      ],
      routingRules: [
        (v) => !this.fields.ach_account_number ? ((!v || v.length === 0) || (v.length === 9 && /^\d+$/.test(v) && this.validateABA(v)) || 'Routing number is invalid') : true,
        (v) => this.fields.ach_account_number ? (!!v || 'Routing number is required') : true,
        (v) => this.fields.ach_account_number && v ? (/^\d+$/.test(v) || 'Routing number must be only digits') : true,
        (v) => this.fields.ach_account_number && v ? (v.length === 9 || 'Routing number must be nine digits') : true,
        (v) => this.fields.ach_account_number && v ? (this.validateABA(v) || 'Routing number is invalid') : true
      ],
      accountRules: [
        (v) => !v || (v.length < 18) || 'Account number must be less than 18 digits'
      ]
    }
  },

  props: {
    terminal: Object,
    schedule: Object
  },

  watch: {
    schedule: {
      handler () {
        this.load()
      },
      immediate: true
    }
  },

  computed: {
    cardMask () {
      if (this.cardType === 'amex' || this.cardType === 'diners' || this.cardType === 'jcb15') {
        return '#### ###### #####'
      }
      return '#### #### #### ####'
    },

    cardType () {
      let amex = /^3[47]\d{0,13}/
      let diners = /^3(?:0([0-5]|9)|[689]\d?)\d{0,11}/
      let discover = /^(?:6011|65\d{0,2}|64[4-9]\d?)\d{0,12}/
      let jcb = /^(?:35\d{0,2})\d{0,12}/
      let jcb15 = /^(?:2131|1800)\d{0,11}/
      let maestro = /^(?:5[0678]\d{0,2}|6304|67\d{0,2})\d{0,12}/
      let mastercard = /^(5[1-5]\d{0,2}|22[2-9]\d{0,1}|2[3-7]\d{0,2})\d{0,12}/
      let unionpay = /^62\d{0,14}/
      let visa = /^4\d{0,15}/

      if (amex.test(this.fields.card_number)) return 'amex'
      else if (diners.test(this.fields.card_number)) return 'diners'
      else if (discover.test(this.fields.card_number)) return 'discover'
      else if (jcb.test(this.fields.card_number)) return 'jcb'
      else if (jcb15.test(this.fields.card_number)) return 'jcb15'
      else if (maestro.test(this.fields.card_number)) return 'maestro'
      else if (mastercard.test(this.fields.card_number)) return 'mastercard'
      else if (unionpay.test(this.fields.card_number)) return 'unionpay'
      else if (visa.test(this.fields.card_number)) return 'visa'
      return 'unknown'
    },

    hasChanges () {
      if (this.fields.card_name !== this.fields.card_name_original) return true

      if (this.schedule.request_type === 'ach') {
        if (this.fields.ach_account_number) return true
        if (this.fields.ach_routing_number !== this.fields.ach_routing_number_original) return true
        if (this.fields.ach_type !== this.fields.ach_type_original) return true
      } else {
        if (this.fields.card_number) return true
        if (this.fields.card_expiration !== this.fields.card_expiration_original) return true
      }

      return false
    }
  },

  methods: {
    closeModal () {
      Bus.$emit('closeModifyModal')
    },

    async load () {
      this.fields.card_name_original = this.fields.card_name = this.schedule.card_name

      if (this.schedule.request_type === 'ach') {
        this.fields.ach_account_number = ''
        this.fields.ach_routing_number_original = this.fields.ach_routing_number = this.schedule.ach_aba
        this.fields.ach_type_original = this.fields.ach_type = this.schedule.ach_account_type
      } else {
        this.fields.card_number = ''
        let yymm = this.schedule.card_expiration_yymm || ''
        let mmyy = yymm.length === 4 ? yymm.slice(2, 4) + yymm.slice(0, 2) : ''
        this.fields.card_expiration_original = this.fields.card_expiration = mmyy
      }
    },

    async save () {
      if (!this.$refs.paymentform.validate()) return

      Bus.$emit('openWaitModal')
      try {
        let options = {}

        if (this.schedule.request_type === 'ach') {
          if (this.fields.ach_account_number) {
            options.ach_account_number = this.fields.ach_account_number
            options.ach_aba = this.fields.ach_routing_number
          } else if (this.fields.ach_routing_number !== this.fields.ach_routing_number_original) {
            options.ach_aba = this.fields.ach_routing_number
          }
          if (this.fields.ach_type !== this.fields.ach_type_original) {
            options.ach_account_type = this.fields.ach_type
          }
        } else {
          let hasNewCard = !!this.fields.card_number
          if (hasNewCard) {
            options.card_number = this.fields.card_number
            options.card_month = this.fields.card_expiration.slice(0, 2)
            options.card_year = this.fields.card_expiration.slice(2, 4)
          } else if (this.fields.card_expiration !== this.fields.card_expiration_original) {
            options.card_month = this.fields.card_expiration.slice(0, 2)
            options.card_year = this.fields.card_expiration.slice(2, 4)
          }
        }

        if (this.fields.card_name !== this.fields.card_name_original) {
          options.card_name = this.fields.card_name
        }

        let result = await this.api({
          method: 'put',
          url: `/schedules/${this.schedule.id}`,
          data: options
        })

        if (result.data && !result.data.error) {
          Bus.$emit('openModal', {status: 'success', message: 'Payment information has been updated'})
          Bus.$emit('reloadPnxInstallment')
          this.closeModal()
        } else {
          throw new Error(result.data.message || 'Error updating payment information')
        }
      } catch (err) {
        Bus.$emit('openModal', {status: 'error', message: err.message || err})
      } finally {
        Bus.$emit('closeWaitModal')
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/pnxInstallments/ModifyPayment.vue